import React, { useState, useEffect, useContext } from 'react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import axiosInstance from '../axiosInstance';
import AuthContext from '../AuthContext';

const pageList = [
  'profile', 'changepassword', 'collection', 'buyer', 'emicalculator',
  'stocks', 'dealer', 'exchange', 'finance', 'viewfinance',
  'seizedvehicle', 'addfinance', 'addexchange', 'addreceipt', 'fullsettlement',
  'user', 'billing', 'cancelbill', 'company', 'dashboard', 'pricing', 'paymenthistory',
  'reports', 'accounts',
];

const PLAN_USER_LIMITS = {
  '1 Month Plan (1 Admin, 1 User)':  2,
  '3 Month Plan (1 Admin, 1 User)':  2,
  '6 Month Plan (1 Admin, 1 User)':  2,
  '1 Year Plan (1 Admin, 1 User)':   2,
  '14 Days Free Trial':              2,
};
const DEFAULT_LIMIT = 2;

const UserManagement = () => {
  const { isPlanAllowed, planName } = useContext(AuthContext);

  // Only offer pages the current plan actually unlocks
  const availablePages = pageList.filter(p => isPlanAllowed(p));
  const [users, setUsers]                   = useState([]);
  const [subscription, setSubscription]     = useState(null);
  const [userLimit, setUserLimit]           = useState(DEFAULT_LIMIT);
  const [formData, setFormData]             = useState({
    name: '', email: '', password: '', confirm_password: '', phone: '', usertype: '3',
  });
  const [errors, setErrors]                 = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showPassword, setShowPassword]     = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [creating, setCreating]             = useState(false);
  const [checkingEmail, setCheckingEmail]   = useState(false);

  const company_id = localStorage.getItem('company_id');
  const user_id    = localStorage.getItem('user_id');

  useEffect(() => {
    fetchUsers();
    fetchSubscription();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get('/user/nosuperadmin');
      const allUsers = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      // Only show active users
      setUsers(allUsers.filter(u => u.isactive !== false));
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    }
  };

  const fetchSubscription = async () => {
    if (!company_id) return;
    try {
      const res = await axiosInstance.get(`/payment/subscription/${company_id}`);
      const sub = res.data?.data;
      setSubscription(sub);
      const limit = PLAN_USER_LIMITS[sub?.plan_name] ?? DEFAULT_LIMIT;
      setUserLimit(limit);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const canAddUser = users.length < userLimit;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ── Dual email check: checks BOTH existing users AND companies ──
  const checkEmailExists = async (email) => {
    setCheckingEmail(true);
    try {
      // Check 1: Email in users of this company
      const userEmailExists = users.some(
        u => u.email?.toLowerCase() === email.toLowerCase()
      );
      if (userEmailExists) {
        setCheckingEmail(false);
        return 'This email is already used by another user in your company';
      }

      // Check 2: Email in /user/email/:email (checks across all users in backend)
      try {
        const res = await axiosInstance.get(`/user/email/${encodeURIComponent(email)}`);
        if (res.data && (res.data.id || res.data.data?.id)) {
          setCheckingEmail(false);
          return 'This email is already registered in the system';
        }
      } catch (err) {
        // 404 = email not found = good
        if (err.response?.status !== 404) {
          console.warn('Email check failed (non-fatal):', err.message);
        }
      }

      // Check 3: Email in companies (GET /company/getcompanybyadmin)
      try {
        const compRes = await axiosInstance.get('/company/getcompanybyadmin');
        const companies = compRes.data?.data || [];
        const companyEmailExists = companies.some(
          c => c.email?.toLowerCase() === email.toLowerCase()
        );
        if (companyEmailExists) {
          setCheckingEmail(false);
          return 'This email is already registered as a company account';
        }
      } catch (err) {
        console.warn('Company email check failed (non-fatal):', err.message);
      }

      setCheckingEmail(false);
      return null; // no conflict
    } catch (err) {
      setCheckingEmail(false);
      return null; // if check itself fails, let backend decide
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const { name, email, password, confirm_password, phone } = formData;

    if (!name.trim())
      newErrors.name = 'Name is required';

    if (!phone.trim())
      newErrors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(phone.trim()))
      newErrors.phone = 'Enter a valid 10-digit mobile number';

    if (!email.trim())
      newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      newErrors.email = 'Enter a valid email address';

    if (!password)
      newErrors.password = 'Password is required';
    else if (password.length < 6)
      newErrors.password = 'Password must be at least 6 characters';

    if (!confirm_password)
      newErrors.confirm_password = 'Please confirm your password';
    else if (password !== confirm_password)
      newErrors.confirm_password = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!canAddUser) {
      Swal.fire({
        title: 'User Limit Reached',
        html: `Your plan <b>"${subscription?.plan_name || 'current plan'}"</b> allows 
               <b>${userLimit} users</b> maximum.<br/><br/>
               You currently have <b>${users.length} active users</b>.<br/>
               Contact us to upgrade your plan and add more users.`,
        icon: 'warning',
        confirmButtonText: 'Contact Us to Upgrade',
        showCancelButton: true,
        cancelButtonText: 'Close',
        confirmButtonColor: '#f5a623',
      }).then(r => {
        if (r.isConfirmed)
          window.open('mailto:support@emiride.in?subject=Upgrade Plan - More Users', '_blank');
      });
      return;
    }

    if (!validateForm()) return;

    // ── Dual email check before hitting the create API ──
    const emailError = await checkEmailExists(formData.email.trim());
    if (emailError) {
      setErrors(prev => ({ ...prev, email: emailError }));
      Swal.fire({
        icon: 'error',
        title: 'Email Already Exists',
        text: emailError,
        confirmButtonColor: '#dc3545',
      });
      return;
    }

    setCreating(true);
    try {
      const res = await axiosInstance.post('/user/adduserbyadmin', {
        username:              formData.name.trim(),
        fullname:              formData.name.trim(),
        email:                 formData.email.trim(),
        password:              formData.password,
        phone:                 formData.phone.trim(),
        whatsappno:            formData.phone.trim(),
        company_id,
        usertype:              parseInt(formData.usertype, 10),
        created_by:            user_id,
        created_by_type:       'Admin',
        force_password_change: false,
      });

      // ── Safely check response — API might return 200 with error body ──
      const resData = res?.data;
      const created = resData?.id
        || resData?.data?.id
        || resData?.result === 'Success'
        || resData?.success === true
        || res.status === 201
        || res.status === 200;

      if (created) {
        Swal.fire({ icon: 'success', title: 'User Created', text: 'User created successfully.', timer: 2000, showConfirmButton: false });
        setFormData({ name: '', email: '', password: '', confirm_password: '', phone: '', usertype: '3' });
        setErrors({});
        fetchUsers();
      } else {
        // API returned 200 but something seems wrong
        const msg = resData?.message || resData?.error || 'Failed to create user';
        Swal.fire('Error', msg, 'error');
      }
    } catch (err) {
      console.error('Create user error:', err);

      // ── Extract meaningful error from any response shape ──
      const errData    = err.response?.data;
      const statusCode = err.response?.status;

      let msg = 'Failed to create user. Please try again.';

      if (errData) {
        msg = errData.message
          || errData.error
          || errData.errors?.[0]?.message
          || errData.errors?.[0]
          || (typeof errData === 'string' ? errData : null)
          || msg;
      }

      // ── Detect email/duplicate errors from backend ──
      const isEmailError =
        statusCode === 409 ||
        msg.toLowerCase().includes('email') ||
        msg.toLowerCase().includes('unique') ||
        msg.toLowerCase().includes('duplicate') ||
        msg.toLowerCase().includes('already');

      if (isEmailError) {
        const emailMsg = 'This email is already registered';
        setErrors(prev => ({ ...prev, email: emailMsg }));
        Swal.fire({
          icon: 'error',
          title: 'Email Already Exists',
          text: emailMsg,
          confirmButtonColor: '#dc3545',
        });
      } else {
        Swal.fire('Error', msg, 'error');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSelectUser = async (userId) => {
    if (selectedUserId === userId) {
      setSelectedUserId(null);
      setSelectedPermissions([]);
      return;
    }
    setSelectedUserId(userId);
    try {
      const res = await axiosInstance.get(`/user/permissions/${userId}`);
      setSelectedPermissions(res.data || []);
    } catch (err) {
      setSelectedPermissions([]);
    }
  };

  const handleCheckboxToggle = (page) => {
    setSelectedPermissions(prev =>
      prev.includes(page) ? prev.filter(p => p !== page) : [...prev, page]
    );
  };

  const handleSavePermissions = async () => {
    try {
      await axiosInstance.post('/user/permissions', {
        user_id: selectedUserId,
        pages: selectedPermissions,
      });
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Permissions updated.', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', 'Error updating permissions', 'error');
    }
  };

  const handleResetPassword = async (userId) => {
    const confirm = await Swal.fire({
      title: 'Reset Password?',
      text: 'Default password will be set to 12345',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Reset!',
    });
    if (!confirm.isConfirmed) return;
    try {
      await axiosInstance.put(`/user/changepassword/${userId}`, {
        new_password: '12345',
        admin_reset: true,
      });
      Swal.fire({ icon: 'success', title: 'Done', text: 'Password reset to 12345', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || err.response?.data?.message || 'Failed to reset password', 'error');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const confirm = await Swal.fire({
      title: 'Delete User?',
      html: `<b>${username}</b> will be deactivated and cannot log in.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete!',
      confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;
    try {
      await axiosInstance.delete(`/user/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (selectedUserId === userId) {
        setSelectedUserId(null);
        setSelectedPermissions([]);
      }
      Swal.fire({ icon: 'success', title: 'Deleted', text: `${username} has been deactivated.`, timer: 1500, showConfirmButton: false });
    } catch (err) {
      console.error('Delete error:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  // ── Reusable field with inline error ──
  const InputField = ({ label, name, type = 'text', placeholder, required, children }) => (
    <div className="form-group">
      <label>{label} {required && <span className="text-danger">*</span>}</label>
      {children ? children : (
        <input
          type={type}
          name={name}
          className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
          value={formData[name]}
          onChange={handleInputChange}
          placeholder={placeholder}
        />
      )}
      {errors[name] && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>{errors[name]}</div>}
    </div>
  );

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">User Management</h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">

          {/* ── User limit banner ── */}
          <div className="row mb-3">
            <div className="col-12">
              <div className={`alert ${canAddUser ? 'alert-info' : 'alert-warning'} d-flex align-items-center justify-content-between mb-0`}
                style={{ borderRadius: '8px' }}>
                <div>
                  <i className={`fa ${canAddUser ? 'fa-users' : 'fa-exclamation-triangle'} mr-2`}></i>
                  <strong>Active Users: {users.length} / {userLimit}</strong>
                  {subscription?.plan_name && (
                    <span className="text-muted ml-2" style={{ fontSize: '13px' }}>({subscription.plan_name})</span>
                  )}
                  {!canAddUser && <span className="ml-2 text-danger" style={{ fontSize: '13px' }}>— Limit reached</span>}
                </div>
                {!canAddUser && (
                  <button className="btn btn-sm btn-warning"
                    onClick={() => window.open('mailto:support@emiride.in?subject=Upgrade Plan - More Users', '_blank')}>
                    <i className="fa fa-arrow-up mr-1"></i> Upgrade Plan
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="row">

            {/* ── Create User Form ── */}
            <div className="col-md-6">
              <div className="card card-primary">
                <div className="card-header">
                  <h3 className="card-title">Create New User</h3>
                </div>
                <div className="card-body">
                  {!canAddUser ? (
                    <div className="text-center py-4">
                      <i className="fa fa-lock fa-3x text-warning mb-3"></i>
                      <h5>User Limit Reached</h5>
                      <p className="text-muted" style={{ fontSize: '14px' }}>
                        Your plan allows <strong>{userLimit} users</strong> maximum.<br/>
                        Contact us to add more users.
                      </p>
                      <button className="btn btn-warning"
                        onClick={() => window.open('mailto:support@emiride.in?subject=Upgrade Plan - More Users', '_blank')}>
                        <i className="fa fa-envelope mr-2"></i>Contact Us to Upgrade
                      </button>
                    </div>
                  ) : (
                    <>
                      <InputField label="Name" name="name" placeholder="Enter full name" required />

                      {/* Email with async check indicator */}
                      <div className="form-group">
                        <label>Email <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <input
                            type="email"
                            name="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter email address"
                          />
                          {checkingEmail && (
                            <div className="input-group-append">
                              <span className="input-group-text">
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </span>
                            </div>
                          )}
                        </div>
                        {errors.email && (
                          <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>
                            <i className="fa fa-exclamation-circle mr-1"></i>{errors.email}
                          </div>
                        )}
                      </div>

                      {/* Password */}
                      <div className="form-group">
                        <label>Password <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Min. 6 characters"
                          />
                          <div className="input-group-append">
                            <button type="button" className="btn btn-outline-secondary"
                              onClick={() => setShowPassword(p => !p)}>
                              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </button>
                          </div>
                        </div>
                        {errors.password && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>{errors.password}</div>}
                      </div>

                      {/* Confirm Password */}
                      <div className="form-group">
                        <label>Confirm Password <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="confirm_password"
                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                            value={formData.confirm_password}
                            onChange={handleInputChange}
                            placeholder="Repeat password"
                          />
                          <div className="input-group-append">
                            <button type="button" className="btn btn-outline-secondary"
                              onClick={() => setShowConfirmPassword(p => !p)}>
                              <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </button>
                          </div>
                        </div>
                        {errors.confirm_password && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>{errors.confirm_password}</div>}
                      </div>

                      <InputField label="Phone" name="phone" placeholder="10-digit mobile number" required />

                      <div className="form-group">
                        <label>User Type <span className="text-danger">*</span></label>
                        <select className="form-control" name="usertype" value={formData.usertype}
                          onChange={handleInputChange}>
                          <option value="2">Admin</option>
                          <option value="3">User</option>
                        </select>
                      </div>

                      <button
                        onClick={handleCreateUser}
                        className="btn btn-success mt-2 w-100"
                        disabled={creating || checkingEmail}
                      >
                        {creating
                          ? <><i className="fa fa-spinner fa-spin mr-2"></i>Creating...</>
                          : checkingEmail
                          ? <><i className="fa fa-spinner fa-spin mr-2"></i>Checking email...</>
                          : <><i className="fa fa-user-plus mr-2"></i>Create User</>}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── User List (active only) ── */}
            <div className="col-md-6">
              <div className="card card-info">
                <div className="card-header">
                  <h3 className="card-title">
                    Existing Users
                    <span className="badge badge-light ml-2" style={{ fontSize: '12px' }}>
                      {users.length} active
                    </span>
                  </h3>
                </div>
                <div className="card-body" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                  {users.length === 0 ? (
                    <p className="text-muted text-center py-3">No active users found.</p>
                  ) : (
                    users.map(user => (
                      <div key={user.id}
                        className="d-flex justify-content-between align-items-center mb-3 pb-2"
                        style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px' }}>{user.username}</div>
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>{user.email}</div>
                        </div>
                        <div className="btn-group">
                          <button className="btn btn-sm btn-primary mx-1" title="Manage Permissions"
                            onClick={() => handleSelectUser(user.id)}>
                            <i className="fas fa-user-shield"></i>
                          </button>
                          <button className="btn btn-sm btn-warning mx-1" title="Reset Password"
                            onClick={() => handleResetPassword(user.id)}>
                            <i className="fas fa-key"></i>
                          </button>
                          <button className="btn btn-sm btn-danger mx-1" title="Delete"
                            onClick={() => handleDeleteUser(user.id, user.username)}>
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Permissions Panel ── */}
          {selectedUserId && (
            <div className="card card-warning mt-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title">
                  Assign Permissions —{' '}
                  <span style={{ color: '#ffc107' }}>
                    {users.find(u => u.id === selectedUserId)?.username}
                  </span>
                  {planName && (
                    <span className="badge badge-info ml-2" style={{ fontSize: '11px', fontWeight: 'normal' }}>
                      {planName}
                    </span>
                  )}
                </h3>
                <button className="btn btn-sm btn-outline-danger"
                  onClick={() => { setSelectedUserId(null); setSelectedPermissions([]); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="card-body">
                <div className="mb-3 d-flex align-items-center" style={{ gap: '8px' }}>
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedPermissions([...availablePages])}>
                    Select All
                  </button>
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedPermissions([])}>
                    Clear All
                  </button>
                  <span className="text-muted" style={{ fontSize: '12px' }}>
                    {selectedPermissions.length}/{availablePages.length} selected
                  </span>
                </div>
                <div className="row" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {availablePages.map((page, index) => (
                    <div className="col-md-4 col-sm-6" key={index}>
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" id={`perm-${page}`}
                          checked={selectedPermissions.includes(page)}
                          onChange={() => handleCheckboxToggle(page)} />
                        <label className="form-check-label" htmlFor={`perm-${page}`}
                          style={{ fontSize: '13px', cursor: 'pointer' }}>
                          {page}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-warning mt-3" onClick={handleSavePermissions}>
                  <i className="fa fa-save mr-2"></i>Save Permissions
                </button>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default UserManagement;